import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCreateSummarizer, useGetSummarizer } from "@packages/api/madlenAPI";
import { getEnvironment } from "@packages/utils";
import { useToast } from "@packages/contexts/ToastContext";
import SummaryCard from "../../../components/summaries/SummaryCard";
import ErrorComponent from "@features/tools/components/error-component";
import { TextLinear, Button } from "@core/index";
import LoadingContainer from "@packages/components/Loading";
import {
  SummarizerCreateRequestDtoGenerationType,
  SummarizerCreateRequestDtoContentLanguage,
  SummarizerCreateRequestDtoCefrLevel,
  TeacherDocumentResponseDto,
} from "@packages/api/madlenAPI.schemas";
import { FileUploader } from "@core/file-uploader";
import { trackEvent } from "@packages/hooks/useAnalytics";
import ModeToggle from "../../../../common/components/mode-toggle";
import { updateLastUsedTools } from "@packages/store/userSlice";
import { useAppDispatch } from "@packages/store/hooks";
import enFlag from "@packages/assets/en.png";
import trFlag from "@packages/assets/tr.png";
import deFlag from "@packages/assets/de.png";
import frFlag from "@packages/assets/fr.png";
import esFlag from "@packages/assets/es.png";

type GeneratorMode = "document" | "text";
const CHARACTER_LIMIT = 200000;

// CEFR level options
const cefrLevelOptions = [
  { value: SummarizerCreateRequestDtoCefrLevel.PRE_A1, label: "Pre-A1" },
  { value: SummarizerCreateRequestDtoCefrLevel.A1, label: "A1" },
  { value: SummarizerCreateRequestDtoCefrLevel.A2, label: "A2" },
  { value: SummarizerCreateRequestDtoCefrLevel.B1, label: "B1" },
  { value: SummarizerCreateRequestDtoCefrLevel.B2, label: "B2" },
  { value: SummarizerCreateRequestDtoCefrLevel.C1, label: "C1" },
  { value: SummarizerCreateRequestDtoCefrLevel.C2, label: "C2" },
];
const CreateSummaryView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [input, setInput] = useState({
    originalText: "",
  });
  const [generatorMode, setGeneratorMode] = useState<GeneratorMode>("text");
  const [uploadedFiles, setUploadedFiles] = useState<
    TeacherDocumentResponseDto[]
  >([]);
  const [contentLanguage, setContentLanguage] = useState<
    "en" | "tr" | "de" | "fr" | "es"
  >(i18n.language as "en" | "tr" | "de" | "fr" | "es");
  const [cefrLevel, setCefrLevel] =
    useState<SummarizerCreateRequestDtoCefrLevel>(
      SummarizerCreateRequestDtoCefrLevel.A1,
    );
  const dispatch = useAppDispatch();
  const { mutateAsync: createSummarizer, data: summarizer } =
    useCreateSummarizer();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { trackEvent: track } = trackEvent();

  const acceptedFileTypes = {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "text/plain": [".txt"],
    "image/png": [".png"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/jpg": [".jpg", ".jpeg"],
  };

  const { data: summarizerData } = useGetSummarizer(summarizer?.id!, {
    query: {
      enabled: !!summarizer?.id,
      refetchInterval: 2000,
    },
  });

  useEffect(() => {
    if (summarizerData?.markdownSummary) {
      setIsLoading(false);
    }
  }, [isLoading, summarizerData]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (
      (generatorMode === "text" && input.originalText.trim() === "") ||
      (generatorMode === "document" && uploadedFiles.length === 0)
    ) {
      addToast({
        duration: 5000,
        title: t("summaries.create.error.empty"),
        variant: "error",
      });
      return;
    }
    if (
      generatorMode === "text" &&
      input.originalText.length > CHARACTER_LIMIT
    ) {
      addToast({
        duration: 5000,
        title: t("summaries.create.error.tooLong"),
        description: t("summaries.create.error.characterLimit", {
          limit: CHARACTER_LIMIT.toLocaleString(),
        }),
        variant: "error",
      });
      return;
    }

    const teacherDocumentIds = uploadedFiles
      .map((file) => file.id)
      .filter((id): id is number => id !== undefined);

    try {
      setIsLoading(true);
      setError(null);

      const result = await createSummarizer({
        data: {
          originalText: input.originalText,
          environment: getEnvironment(),
          teacherDocumentIds,
          contentLanguage:
            contentLanguage as SummarizerCreateRequestDtoContentLanguage,
          cefrLevel: isCheckOutputLanguage(
            contentLanguage,
          ) as SummarizerCreateRequestDtoCefrLevel,
          generationType:
            generatorMode === "document"
              ? SummarizerCreateRequestDtoGenerationType.TEACHER_DOCUMENT
              : SummarizerCreateRequestDtoGenerationType.PROMPT,
        },
      });

      if (!result?.id) {
        console.error("No summarizer ID returned");
      }
      dispatch(updateLastUsedTools("summaries"));
    } catch (error: any) {
      setIsLoading(false);
      if (
        error.response?.data.message ===
        "Content contains inappropriate language"
      ) {
        addToast({
          title: t("common.inappropriateContent.title"),
          description: t("common.inappropriateContent.description"),
          variant: "error",
          duration: 3000,
        });
      } else {
        setError(error as Error);
        console.error("Summarizer error:", error);
        addToast({
          title: t("summaries.create.error.title"),
          description: t("summaries.create.error.description"),
          variant: "error",
        });
      }
    }
  };

  const handleFileUploadError = (error: string) => {
    addToast({
      title: t("assistant.uploadError"),
      description: error,
      variant: "error",
      duration: 5000,
    });
  };

  const handleFileUploadSuccess = async (
    url: string,
    response?: TeacherDocumentResponseDto,
  ) => {
    track(
      {
        category: "ContentFileUpload",
        action: `summaries.create.file.upload.success`,
        label: `SummariesCreate file upload success`,
        nonInteraction: false,
      },
      {
        location: "/summaries-create/file-upload",
        element: "file-upload",
      },
    );

    if (!response) {
      console.error("No response received from upload");
      addToast({
        title: t("assistant.uploadError"),
        description: t("assistant.uploadErrorDescription"),
        variant: "error",
        duration: 5000,
      });
      return;
    }

    setUploadedFiles((prevFiles) => {
      const newFiles = [...prevFiles, response];
      return newFiles;
    });
  };

  if (summarizerData?.markdownSummary) {
    return (
      <div className="container max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-start items-center mb-8 flex-col sm:flex-row">
          <Button
            onClick={() => navigate("/content/summaries")}
            variant="solid"
            size="md"
            textColor="text-white"
            bgColor="bg-teal-500 hover:bg-teal-600"
            rounded="lg"
            className="mb-8 sm:mb-0"
          >
            <ArrowLeft size={24} />
          </Button>
        </div>

        <SummaryCard
          onCreateAgain={() => {
            handleSubmit();
          }}
          createdAt={summarizerData?.createdAt ?? ""}
          content={summarizerData?.markdownSummary}
          imageUrl={summarizerData?.imageUrl}
          videoUrl={summarizerData?.videoUrl}
          author={summarizerData?.authorName ?? ""}
          cefrLevel={cefrLevel}
          contentLanguage={contentLanguage}
          title={summarizerData?.title}
        />
      </div>
    );
  }

  const isCheckOutputLanguage = (language: string) => {
    return ["en", "fr", "de", "es"].includes(language)
      ? cefrLevel
      : SummarizerCreateRequestDtoCefrLevel.A1;
  };

  if (isLoading) {
    return (
      <LoadingContainer
        title={t("summaries.create.loading.title")}
        themeColor="teal"
      />
    );
  }

  if (error) {
    return (
      <ErrorComponent
        title={t("summaries.create.error.title")}
        description={t("summaries.create.error.description")}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50 dark:from-gray-900 dark:to-teal-900">
      <div className="container max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center mb-8 sm:mb-0">
          <Button
            variant="solid"
            size="md"
            rounded="lg"
            bgColor="bg-teal-500 dark:bg-teal-600 hover:bg-teal-600 dark:hover:bg-teal-700"
            textColor="text-white dark:text-white"
            leftIcon={<ArrowLeft size={24} />}
            onClick={() => navigate("/content/summaries")}
          />
        </div>

        {/* Input Form Card */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-200/50 dark:border-gray-700/50">
            <form onSubmit={handleSubmit} className="space-y-8">
              <ModeToggle
                value={generatorMode}
                onChange={(value) => {
                  setGeneratorMode(value as GeneratorMode);
                  setInput({ ...input, originalText: "" });
                  setUploadedFiles([]);
                }}
                title={t("podcast.selectInputType")}
                theme="green"
              />

              {/* Content Language Selector */}
              <div className="space-y-3">
                <TextLinear
                  variant={"label"}
                  from="from-teal-600 dark:from-teal-400"
                  to="to-teal-500 dark:to-teal-300"
                  className="inline-block"
                >
                  {t("summaries.contentLanguage")}
                </TextLinear>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Button
                    type="button"
                    onClick={() => setContentLanguage("tr")}
                    rounded="xl"
                    bgColor={
                      contentLanguage === "tr"
                        ? "bg-teal-50 dark:bg-teal-900/20"
                        : ""
                    }
                    className={`p-3 border-2 ${
                      contentLanguage === "tr"
                        ? "border-teal-500"
                        : "border-gray-200 hover:border-teal-400"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <img
                        src={trFlag}
                        alt="Turkish"
                        className="w-6 h-6 object-contain rounded"
                      />
                      <span className="text-sm sm:text-base">Türkçe</span>
                    </div>
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setContentLanguage("en")}
                    rounded="xl"
                    bgColor={
                      contentLanguage === "en"
                        ? "bg-teal-50 dark:bg-teal-900/20"
                        : ""
                    }
                    className={`p-3 border-2 ${
                      contentLanguage === "en"
                        ? "border-teal-500"
                        : "border-gray-200 hover:border-teal-400"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <img
                        src={enFlag}
                        alt="English"
                        className="w-6 h-6 object-contain rounded"
                      />
                      <span className="text-sm sm:text-base">English</span>
                    </div>
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setContentLanguage("de")}
                    rounded="xl"
                    bgColor={
                      contentLanguage === "de"
                        ? "bg-teal-50 dark:bg-teal-900/20"
                        : ""
                    }
                    className={`p-3 border-2 ${
                      contentLanguage === "de"
                        ? "border-teal-500"
                        : "border-gray-200 hover:border-teal-400"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <img
                        src={deFlag}
                        alt="German"
                        className="w-6 h-6 object-contain rounded"
                      />
                      <span className="text-sm sm:text-base">Deutsch</span>
                    </div>
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setContentLanguage("fr")}
                    rounded="xl"
                    bgColor={
                      contentLanguage === "fr"
                        ? "bg-teal-50 dark:bg-teal-900/20"
                        : ""
                    }
                    className={`p-3 border-2 ${
                      contentLanguage === "fr"
                        ? "border-teal-500"
                        : "border-gray-200 hover:border-teal-400"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <img
                        src={frFlag}
                        alt="French"
                        className="w-6 h-6 object-contain rounded"
                      />
                      <span className="text-sm sm:text-base">Français</span>
                    </div>
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setContentLanguage("es")}
                    rounded="xl"
                    bgColor={
                      contentLanguage === "es"
                        ? "bg-rose-50 dark:bg-rose-900/20"
                        : ""
                    }
                    className={`p-2 sm:p-3 border-2 ${
                      contentLanguage === "es"
                        ? "border-rose-500"
                        : "border-gray-200 hover:border-rose-400"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <img
                        src={esFlag}
                        alt="Spanish"
                        className="w-4 h-4 rounded-full object-cover shadow-sm"
                      />
                      <span className="text-sm sm:text-base">Español</span>
                    </div>
                  </Button>
                </div>
              </div>

              {/* CEFR Level Selector - Only show for English */}
              {["en", "fr", "de", "es"].includes(contentLanguage) && (
                <div className="space-y-3 animate-fadeIn">
                  <TextLinear
                    variant={"label"}
                    from="from-teal-600 dark:from-teal-400"
                    to="to-teal-500 dark:to-teal-300"
                    className="inline-block"
                  >
                    {t("summaries.cefrLevel")}
                  </TextLinear>
                  <div className="space-y-2">
                    {/* First row: 4 levels (Pre-A1, A1, A2, B1) */}
                    <div className="grid grid-cols-4 gap-2">
                      {cefrLevelOptions.slice(0, 4).map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          onClick={() => setCefrLevel(option.value)}
                          rounded="xl"
                          bgColor={
                            cefrLevel === option.value
                              ? "bg-teal-50 dark:bg-teal-900/20"
                              : ""
                          }
                          className={`p-2 sm:p-3 border-2 ${
                            cefrLevel === option.value
                              ? "border-teal-500"
                              : "border-gray-200 hover:border-teal-400"
                          }`}
                        >
                          <div className="flex items-center justify-center">
                            <span className="text-sm sm:text-base font-medium">
                              {option.label}
                            </span>
                          </div>
                        </Button>
                      ))}
                    </div>
                    {/* Second row: 3 levels (B2, C1, C2) */}
                    <div className="grid grid-cols-3 gap-2">
                      {cefrLevelOptions.slice(4).map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          onClick={() => setCefrLevel(option.value)}
                          rounded="xl"
                          bgColor={
                            cefrLevel === option.value
                              ? "bg-teal-50 dark:bg-teal-900/20"
                              : ""
                          }
                          className={`p-2 sm:p-3 border-2 ${
                            cefrLevel === option.value
                              ? "border-teal-500"
                              : "border-gray-200 hover:border-teal-400"
                          }`}
                        >
                          <div className="flex items-center justify-center">
                            <span className="text-sm sm:text-base font-medium">
                              {option.label}
                            </span>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <TextLinear
                  variant={"label"}
                  from="from-teal-600 dark:from-teal-400"
                  to="to-teal-500 dark:to-teal-300"
                  className="inline-block"
                >
                  {generatorMode === "text"
                    ? t("summaries.create.originalText")
                    : t("summaries.create.uploadDocumentTitle")}
                </TextLinear>

                {generatorMode === "document" && (
                  <FileUploader
                    accept={acceptedFileTypes}
                    onUploadSuccess={handleFileUploadSuccess}
                    onFileDelete={() => setUploadedFiles([])}
                    onUploadError={handleFileUploadError}
                    dragActiveText={t("summaries.create.dropDocument")}
                    dragInactiveText={t("summaries.create.uploadDocument")}
                    uploadColor="green"
                    extractColor="green"
                    successColor="green"
                    shouldExtractContent
                  />
                )}

                {generatorMode === "text" && (
                  <textarea
                    value={input.originalText}
                    data-testid="lesson-topic-input"
                    onChange={(e) =>
                      setInput({ ...input, originalText: e.target.value })
                    }
                    rows={12}
                    className="w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200"
                    placeholder={t("summaries.create.originalTextPlaceholder")}
                  />
                )}
              </div>

              <Button
                variant="solid"
                size="lg"
                rounded="xl"
                fullWidth
                bgColor="bg-teal-500 hover:bg-teal-600"
                textColor="text-white"
                className="shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:ring-4 focus:ring-teal-500/50"
                type="submit"
                disabled={
                  isLoading ||
                  (generatorMode === "text" && !input.originalText) ||
                  (generatorMode === "document" && uploadedFiles.length === 0)
                }
                data-testid="toolbox-create-button"
              >
                {t("summaries.create.generateSummary")}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSummaryView;
